import { useLayoutEffect, useRef } from "react";

/** 读设计系统的动效令牌（--motion-move、--ease-out），没加载令牌时（如单测）用同值兜底。 */
function motion(): { duration: number; easing: string } {
  const css = getComputedStyle(document.documentElement);
  return { duration: parseFloat(css.getPropertyValue("--motion-move")) || 180, easing: css.getPropertyValue("--ease-out").trim() || "cubic-bezier(0.2, 0, 0, 1)" };
}

/** 列表重排的位移动画（FLIP）：改顺序前 snapshot() 记下各行的位置，重排渲染后每行从旧位置滑到新位置。
 * 只在 snapshot 之后的那次渲染播放，输入、筛选等别的变化不动；系统要求减少动态效果时不播放。行用 ref(key) 挂上。
 * 时长与缓动取自动效令牌；动画只改 transform，不影响焦点与 DOM 次序。 */
export function useFlip<K>() {
  const nodes = useRef(new Map<K, HTMLElement>());
  const before = useRef<Map<K, number> | null>(null);
  useLayoutEffect(() => {
    const prev = before.current;
    before.current = null;
    if (!prev || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const timing = motion();
    for (const [key, el] of nodes.current) {
      const top = prev.get(key);
      const dy = top === undefined ? 0 : top - el.getBoundingClientRect().top;
      if (dy) el.animate?.([{ transform: `translateY(${dy}px)` }, { transform: "none" }], timing);
    }
  });
  const ref = (key: K) => (el: HTMLElement | null) => {
    if (el) nodes.current.set(key, el);
    else nodes.current.delete(key);
  };
  const snapshot = () => {
    before.current = new Map([...nodes.current].map(([key, el]) => [key, el.getBoundingClientRect().top]));
  };
  return { ref, snapshot };
}
